import { createContext, useContext, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPlans, deletePlan, updatePlan } from "../../api/plans";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";
import { useNavigate } from "react-router-dom";
import { getPricing, updatePricing } from "../../api/home";
import { generatePayload, getIntialValues } from "./helper";
import { getFaqs } from "../../api/faq";
import { useRef, useMemo } from "react";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const toaster = useToaster();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const communityArrayRef = useRef();
  const workflowArrayRef = useRef();
  const liveStreamArrayRef = useRef();
  const featureArrayRef = useRef();
  const aiAgentsArrayRef = useRef();
  const monialsRef = useRef();
  const [page, setPage] = useState(1);

  const getPlansQuery = useQuery({
    queryKey: [{ page }],
    queryFn: getPlans,
    keepPreviousData: true,
  });

  const getPricingQuery = useQuery({
    queryKey: ["plan-query-api", {}],
    queryFn: getPricing,
  });

  const getFaqQuery = useQuery({
    queryKey: [{ limit: 200 }],
    queryFn: getFaqs,
  });

  const updatePricingQuery = useMutation({ mutationFn: updatePricing });
  const updatePlanQuery = useMutation({ mutationFn: updatePlan });
  const deletePlanQuery = useMutation({ mutationFn: deletePlan });

  const initialValues = useMemo(() => {
    const homeData = getPricingQuery?.data?.data?.data?.[0] || {};

    return getIntialValues(homeData.page_content);
  }, [getPricingQuery]);

  const handleSubmit = async (values) => {
    const payload = generatePayload(values);
    const homeData = getPricingQuery?.data?.data?.data?.[0] || {};

    updatePricingQuery.mutate(
      { page_content: payload, id: homeData.id },
      {
        onSuccess: () => {
          toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);
          getPricingQuery.refetch();
        },
      },
    );
    return;
  };

  const handleWorkDelete = (item) => {
    deletePlanQuery.mutate(item.id, {
      onSuccess: () => {
        queryClient.invalidateQueries(["blogs"]);
        toaster.success(Const.MESSAGES.SUCCESS, "Blog is deleted successfully.");
      },
    });
  };

  const handleAddNewClick = () => {
    navigate("/add-plan");
  };

  const handleEditClick = (item) => {
    navigate(`/add-plan?id=${item.id}`);
  };

  const handleOnChange = (e) => {
    const { value } = e.target;
    return value;
  };

  const handleCommunityAdd = () => {
    communityArrayRef.current?.push({
      title: "",
      community_title: "",
      free: "",
      standard: "",
      advanced: "",
    });
  };

  const handleCommunityDelete = (index) => () => {
    communityArrayRef.current?.remove(index);
  };

  const handleWorkflowAdd = () => {
    workflowArrayRef.current?.push({
      title: "",
      workflow_title: "",
      free: "",
      standard: "",
      advanced: "",
    });
  };

  const handleWorkflowDelete = (index) => () => {
    workflowArrayRef.current?.remove(index);
  };

  const handleLiveStreamAdd = () => {
    liveStreamArrayRef.current?.push({
      title: "",
      live_title: "",
      free: "",
      standard: "",
      advanced: "",
    });
  };

  const handleLiveStreamDelete = (index) => () => {
    liveStreamArrayRef.current?.remove(index);
  };

  const handleFeatureAdd = () => {
    featureArrayRef.current?.push({
      title: "",
      live_title: "",
      free: "",
      standard: "",
      advanced: "",
    });
  };

  const handleFeatureDelete = (index) => () => {
    featureArrayRef.current?.remove(index);
  };

  const handleAIAgentAdd = () => {
    aiAgentsArrayRef.current?.push({
      title: "",
      agents_title: "",
      free: "",
      standard: "",
      advanced: "",
    });
  };

  const handleAIAgentDelete = (index) => () => {
    aiAgentsArrayRef.current?.remove(index);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const handleSeqChange =(item) => (e) => {
    updatePlanQuery.mutate({ seq_no: e.target.value, id: item.id})
  }

  return (
    <PageContext.Provider
      value={{
        getPlansQuery,
        handleWorkDelete,
        handleAddNewClick,
        handleEditClick,
        handleOnChange,
        communityArrayRef,
        handleCommunityAdd,
        handleCommunityDelete,
        handleSubmit,
        initialValues,
        monialsRef,
        getFaqQuery,
        workflowArrayRef,
        handleWorkflowAdd,
        handleWorkflowDelete,
        liveStreamArrayRef,
        handleLiveStreamAdd,
        handleLiveStreamDelete,
        handleFeatureAdd,
        featureArrayRef,
        handleFeatureDelete,
        aiAgentsArrayRef,
        handleAIAgentAdd,
        handleAIAgentDelete,
        handlePageChange,
        currentPage: page,
        handleSeqChange
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const useBlogsProvider = () => {
  return useContext(PageContext);
};
